import { FormikProvider, useFormik } from 'formik'
import React, { FC, useState } from 'react'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'

import {
  ProfileAvatar,
  useQueryProfileCountry
} from 'src/features/profiles/common'
import { useProfileFactoryCreateStyles } from './profile-factory-create.styles'
import {
  profileFactoryCreateValidationSchemas,
  FactoryCreateProfileForm,
  useMutationCreateProfileFactory,
  ProfileFactoryActivitySpecialization
} from '../common'
import { CropPictureModal } from 'src/common/crop-picture-modal'
import { BreadCrumbs } from 'src/features/breadcrumbs/breadcrumbs'
import {
  AutocompleteWrapper,
  TranslationDictionary,
  // getServiceURL,
  SERVICES_URL
} from '@plandi/common'

interface IProfileFactoryCreateProps {
  locale: string
  translate?: TranslationDictionary['data']
}

export const ProfileFactoryCreate: FC<IProfileFactoryCreateProps> = ({
  locale,
  translate
}) => {
  const classes = useProfileFactoryCreateStyles()
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const url = SERVICES_URL

  const { data: countries } = useQueryProfileCountry()
  const mutationCreateProfileFactory = useMutationCreateProfileFactory()

  const formik = useFormik<FactoryCreateProfileForm>({
    initialValues: {
      avatarUrl: undefined,
      name: '',
      about: '',
      countryId: undefined,
      mainActivitySpecializations: [],
      activitySpecializations: [],
      skills: []
    },
    enableReinitialize: true,
    validationSchema:
      profileFactoryCreateValidationSchemas(translate)[currentStepIndex],
    onSubmit: async (values, { setSubmitting, validateForm }) => {
      if (currentStepIndex !== 1) {
        formik.validateForm()
        setCurrentStepIndex(currentStepIndex + 1)

        setSubmitting(false)
        validateForm()
        return
      }

      mutationCreateProfileFactory.mutate(values)
    }
  })

  const handlePrevPage = () => {
    if (currentStepIndex !== 0) {
      setCurrentStepIndex(currentStepIndex - 1)
      formik.setErrors({})
    }
    formik.validateForm()
  }
  const selectedCountry = countries?.find(
    (country) => country.id === formik.values.countryId
  )

  return (
    <FormikProvider value={formik}>
      <BreadCrumbs
        breadcrumbInfo={[
          {
            title: translate?.['job']?.['title2'] ?? 'Фриланс и Работа',
            href:
              locale === 'ru'
                ? `${url.job}/ru/applicants`
                : `${url.job}/applicants`
          },
          {
            title: translate?.['job-site']?.factories ?? 'Заводы',
            href:
              locale === 'ru'
                ? `${url.job}/ru/factories`
                : `${url.job}/factories`
          },
          {
            title:
              translate?.['personal-account']?.['my-ankets'] ?? 'Мои анкеты',
            href:
              locale === 'ru'
                ? `${url.job}/ru${'/questionnaires'}`
                : url.job + '/questionnaires'
          },
          {
            title:
              translate?.['job-site']?.['creating-factory-profile'] ??
              'Создание анкеты завода',
            href: ''
          }
        ]}
      />
      <div className={classes.root}>
        <Typography variant="h4" className={classes.title} align="center">
          {currentStepIndex === 0
            ? translate?.['job-site']?.['creating-factory-profile'] ??
              'Создание анкеты завода'
            : locale === 'ru'
            ? 'Вид деятельности и категоря'
            : 'Type of activity and category'}
        </Typography>
        <div className={classes.wrapper}>
          <form onSubmit={formik.handleSubmit}>
            {currentStepIndex === 0 && (
              <>
                <ProfileAvatar
                  src={formik.values.avatarUrl}
                  invisibleBadge={true}
                  error={Boolean(formik.errors.avatarUrl)}
                />
                <CropPictureModal
                  onUploadImage={(data) =>
                    formik.setFieldValue('avatarUrl', data.url)
                  }
                  uploadOptions={{ isAvatar: true }}
                  CSSClasses={{ label: classes.buttonUpload }}
                  translate={translate}
                  buttonText={
                    locale === 'ru'
                      ? 'Загрузить логотип завода'
                      : 'Upload factory logo'
                  }
                />
                <TextField
                  label={`${
                    translate?.['job-site']?.['factory-name'] ??
                    'Название завода'
                  } *`}
                  variant="outlined"
                  name="name"
                  fullWidth
                  margin="normal"
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  onBlur={formik.handleBlur('name')}
                  error={Boolean(formik.errors.name) && formik.touched.name}
                  helperText={
                    formik.errors.name && formik.touched.name
                      ? formik.errors.name
                      : ' '
                  }
                  disabled={formik.isSubmitting}
                />
                <TextField
                  label={`${
                    translate?.['job-site']?.['about-factory'] ?? 'О заводе'
                  } *`}
                  variant="outlined"
                  name="about"
                  fullWidth
                  margin="normal"
                  onChange={formik.handleChange}
                  value={formik.values.about}
                  helperText={
                    formik.errors.about && formik.touched.about
                      ? formik.errors.about
                      : ' '
                  }
                  onBlur={formik.handleBlur('about')}
                  error={Boolean(formik.errors.about) && formik.touched.about}
                  disabled={formik.isSubmitting}
                  multiline
                  minRows={3}
                />
                <AutocompleteWrapper
                  options={countries ?? []}
                  freeSolo
                  getOptionLabel={(option) => option.name || ''}
                  value={selectedCountry}
                  disabled={formik.isSubmitting}
                  onChange={(_, country) => {
                    if (!country) return
                    formik.setFieldValue(
                      'countryId',
                      Array.isArray(country)
                        ? (country[0] as { id: number; name: string }).id
                        : (country as { id: number; name: string }).id
                    )
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`${translate?.country ?? 'Страна'} *`}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      error={
                        Boolean(formik.errors.countryId) &&
                        formik.touched.countryId
                      }
                      helperText={
                        formik.errors.countryId && formik.touched.countryId
                          ? formik.errors.countryId
                          : ''
                      }
                    />
                  )}
                ></AutocompleteWrapper>
              </>
            )}
            {currentStepIndex === 1 && (
              <div className={classes.row}>
                <ProfileFactoryActivitySpecialization translate={translate} />
              </div>
            )}
            <div className={classes.action}>
              {currentStepIndex > 0 && (
                <Button
                  variant="text"
                  onClick={handlePrevPage}
                  disabled={formik.isSubmitting}
                  style={{ marginTop: 24 }}
                  type="button"
                >
                  {translate?.shop?.back ?? 'Назад'}
                </Button>
              )}
              <Button
                className={classes.button}
                type="submit"
                variant="contained"
                color="secondary"
                disabled={formik.isSubmitting || !formik.dirty}
              >
                {currentStepIndex !== 1
                  ? translate?.['job-site']?.['next-step'] ?? 'Следующий шаг'
                  : translate?.confirm ?? 'Подтвердить'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </FormikProvider>
  )
}
